import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import InvoicesList from './pages/InvoicesList';
import InvoicesAdd from './pages/InvoicesAdd';
import InvoicesByStatus from './pages/InvoicesByStatus';
import InvoicesReturns from './pages/InvoicesReturns';
import InvoicesTrash from './pages/InvoicesTrash';
import InvoiceDescriptionView from './pages/InvoiceDescriptionView';
import UmowyCru from './pages/UmowyCru';
import InvoicePreview from './pages/InvoicePreview';
import PublicInvoicePreview from './pages/PublicInvoicePreview';
import PublicDoctorAbsence from './pages/PublicDoctorAbsence';
import DoctorAbsencesList from './pages/DoctorAbsencesList';
import AttachmentPreview from './pages/AttachmentPreview';
import CasesByStatus from './pages/CasesByStatus';
import DepartmentsList from './pages/DepartmentsList';
import DepartmentsAdd from './pages/DepartmentsAdd';
import DepartmentsUsers from './pages/DepartmentsUsers';
import UsersList from './pages/UsersList';
import UsersAdd from './pages/UsersAdd';
import UsersDepartments from './pages/UsersDepartments';
import SubstitutionsList from './pages/SubstitutionsList';
import Settings from './pages/Settings';
import MfaSettings from './pages/MfaSettings';
import SecurityLogs from './pages/SecurityLogs';
import KsefInvoices from './pages/KsefInvoices';
import EmployeesList from './pages/EmployeesList';
import EmployeesAdd from './pages/EmployeesAdd';
import TimeEntries from './pages/TimeEntries';
import Grafik from './pages/Grafik';
import LeaveRequestsAdmin from './pages/LeaveRequestsAdmin';
import LeaveSupervisorInbox from './pages/LeaveSupervisorInbox';
import LeaveSupervisors from './pages/LeaveSupervisors';
import TicketsList from './pages/TicketsList';
import Prints from './pages/Prints';
import TrainingPortal from './pages/TrainingPortal';
import AdminTrainings from './pages/AdminTrainings';
import LoadingSpinner from './components/LoadingSpinner';

function SecurityLogsAdminRoute() {
  const { user } = useAuth();
  if (user?.role !== 'SUPERADMIN' && user?.role !== 'ADMIN') {
    return <Navigate to="/app/hr/grafik" replace />;
  }
  return <SecurityLogs />;
}

// Strona startowa po zalogowaniu: kto ma dostęp do Faktur, trafia na listę faktur;
// pozostali (np. nowe konta z samym grafikiem) - na Kadry -> Mój grafik.
function DefaultLanding() {
  const { user } = useAuth();
  const canInvoices = user?.role === 'SUPERADMIN' || user?.canAccessInvoices === true;
  return <Navigate to={canInvoices ? '/app/invoices/list' : '/app/hr/grafik'} replace />;
}

/** Ścieżki publiczne – nie blokujemy UI na weryfikacji tokenu (lekarz bez sesji CRM ma od razu formularz). */
function isPublicStandalonePath(pathname) {
  if (pathname === '/login') return true;
  if (pathname === '/doctor-absence') return true;
  if (pathname.startsWith('/szkolenia')) return true;
  if (pathname.startsWith('/invoice/')) return true;
  return false;
}

function AppRoutes() {
  const { pathname } = useLocation();
  const { user, loading } = useAuth();

  if (loading && !isPublicStandalonePath(pathname)) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <Routes>
      <Route 
        path="/login" 
        element={user ? <Navigate to={user.requiresMfaSetup ? "/app/mfa-settings" : "/app/invoices/list"} replace /> : <Login />} 
      />
      
      {/* Publiczny podgląd faktury - bez logowania */}
      <Route 
        path="/invoice/:id" 
        element={<PublicInvoicePreview />} 
      />

      {/* Publiczny formularz zgłoszenia nieobecności lekarza - bez logowania */}
      <Route
        path="/doctor-absence"
        element={<PublicDoctorAbsence />}
      />

      {/* Portal szkoleń dla pracowników (logowanie kontem domenowym AD) - osobna sesja */}
      <Route
        path="/szkolenia"
        element={<TrainingPortal />}
      />
      
      {/* Podgląd faktury - bez Layout (osobne okno) */}
      <Route 
        path="/app/invoices/:id/preview" 
        element={user ? <InvoicePreview /> : <Navigate to="/login" replace />} 
      />
      
      {/* Podgląd załącznika - bez Layout (osobne okno) */}
      <Route 
        path="/app/attachments/:id/preview" 
        element={user ? <AttachmentPreview /> : <Navigate to="/login" replace />} 
      />
      
      <Route 
        path="/*" 
        element={
          user ? (
            <Layout>
              <Routes>
                <Route path="/app/dashboard" element={<Dashboard />} />
                
                {/* Faktury */}
                {/* Faktury - moduł dodatkowy (nowe konta bez uprawnienia nie mają dostępu) */}
                <Route path="/app/invoices/list" element={<ProtectedRoute requiredPermission="Invoices"><InvoicesList /></ProtectedRoute>} />
                <Route path="/app/invoices/add" element={<ProtectedRoute requiredPermission="Invoices"><InvoicesAdd /></ProtectedRoute>} />
                <Route path="/app/invoices/returns" element={<ProtectedRoute requiredPermission="Invoices"><InvoicesReturns /></ProtectedRoute>} />
                <Route path="/app/invoices/trash" element={<ProtectedRoute requiredPermission="Invoices"><InvoicesTrash /></ProtectedRoute>} />
                <Route path="/app/invoices/status/:status" element={<ProtectedRoute requiredPermission="Invoices"><InvoicesByStatus /></ProtectedRoute>} />
                <Route path="/app/invoices/:id/description" element={<ProtectedRoute requiredPermission="Invoices"><InvoiceDescriptionView /></ProtectedRoute>} />
                <Route path="/app/invoices/cru" element={
                  <ProtectedRoute requiredPermission="UmowyCru">
                    <UmowyCru />
                  </ProtectedRoute>
                } />
                
                {/* Sprawy */}
                <Route path="/app/cases/status/:status" element={
                  <ProtectedRoute requiredPermission="Cases">
                    <CasesByStatus />
                  </ProtectedRoute>
                } />
                
                {/* Działy */}
                <Route path="/app/departments/list" element={
                  <ProtectedRoute requiredPermission="Departments">
                    <DepartmentsList />
                  </ProtectedRoute>
                } />
                <Route path="/app/departments/add" element={
                  <ProtectedRoute requiredPermission="Departments">
                    <DepartmentsAdd />
                  </ProtectedRoute>
                } />
                <Route path="/app/departments/users" element={
                  <ProtectedRoute requiredPermission="Departments">
                    <DepartmentsUsers />
                  </ProtectedRoute>
                } />
                
                {/* Użytkownicy */}
                <Route path="/app/users/list" element={
                  <ProtectedRoute requiredPermission="Users">
                    <UsersList />
                  </ProtectedRoute>
                } />
                <Route path="/app/users/add" element={
                  <ProtectedRoute requiredPermission="Users">
                    <UsersAdd />
                  </ProtectedRoute>
                } />
                <Route path="/app/users/departments" element={
                  <ProtectedRoute requiredPermission="Users">
                    <UsersDepartments />
                  </ProtectedRoute>
                } />
                
                {/* Zgłoszenia */}
                <Route path="/app/tickets" element={<TicketsList />} />
                
                {/* Zastępstwa */}
                <Route path="/app/substitutions" element={<ProtectedRoute requiredPermission="Substitutions"><SubstitutionsList /></ProtectedRoute>} />
                
                {/* Szkolenia - panel administratora */}
                <Route path="/app/trainings" element={
                  <ProtectedRoute requiredPermission="Trainings">
                    <AdminTrainings />
                  </ProtectedRoute>
                } />

                {/* Wydruki */}
                <Route path="/app/prints" element={
                  <ProtectedRoute requiredPermission="Prints">
                    <Prints />
                  </ProtectedRoute>
                } />
                
                {/* Ustawienia */}
                <Route path="/app/settings" element={
                  <ProtectedRoute requiredPermission="Settings">
                    <Settings />
                  </ProtectedRoute>
                } />
                <Route path="/app/settings/:type" element={
                  <ProtectedRoute requiredPermission="Settings">
                    <Settings />
                  </ProtectedRoute>
                } />
                
                {/* Konfiguracja MFA - dostępne dla wszystkich zalogowanych użytkowników */}
                <Route path="/app/mfa-settings" element={
                  <ProtectedRoute allowMfaSetup>
                    <MfaSettings />
                  </ProtectedRoute>
                } />

                {/* Dziennik bezpieczeństwa - tylko administratorzy */}
                <Route path="/app/security-logs" element={
                  <ProtectedRoute superAdminOnly={false}>
                    <SecurityLogsAdminRoute />
                  </ProtectedRoute>
                } />
                
                {/* Faktury z KSeF */}
                <Route path="/app/ksef/invoices" element={
                  <ProtectedRoute requiredPermission="Settings">
                    <KsefInvoices />
                  </ProtectedRoute>
                } />
                
                {/* Kadry */}
                <Route path="/app/hr/grafik" element={<Grafik />} />
                <Route path="/app/hr/wnioski-urlopowe" element={
                  <ProtectedRoute requiredPermission="LeaveApprovals">
                    <LeaveRequestsAdmin />
                  </ProtectedRoute>
                } />
                {/* Wnioski do podpisu - backend zwraca tylko wnioski, które użytkownik może podpisać */}
                <Route path="/app/hr/wnioski-do-podpisu" element={<LeaveSupervisorInbox />} />
                <Route path="/app/hr/przelozeni" element={
                  <ProtectedRoute requiredPermission="LeaveApprovals">
                    <LeaveSupervisors />
                  </ProtectedRoute>
                } />
                <Route path="/app/hr/employees/list" element={<EmployeesList />} />
                <Route path="/app/hr/employees/add" element={<EmployeesAdd />} />
                <Route path="/app/hr/time-entries/:employeeId" element={<TimeEntries />} />
                <Route path="/app/hr/doctor-absences" element={
                  <ProtectedRoute requiredPermission="DoctorAbsences">
                    <DoctorAbsencesList />
                  </ProtectedRoute>
                } />
                
                <Route path="/" element={<DefaultLanding />} />
                <Route path="*" element={<DefaultLanding />} />
              </Routes>
            </Layout>
          ) : (
            <Navigate to="/login" replace />
          )
        } 
      />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <ThemeProvider>
          <AppRoutes />
        </ThemeProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
